import {
  forwardRef,
  useState,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

export interface TextInputProps
  extends Omit<ComponentPropsWithoutRef<"input">, "size"> {
  wrapperRef?: ForwardedRef<HTMLDivElement>;
  size?: "sm" | "md" | "lg";
  wrapperClassName?: string;
  className?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      type = "text",
      wrapperRef,
      size = "sm",
      value,
      disabled,
      wrapperClassName,
      className,
      leftIcon,
      rightIcon,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [hasFocus, setHasFocus] = useState(false);

    return (
      <div
        ref={wrapperRef}
        className={twMerge(
          clsx(
            "group bg-v2-primary text-v2-primary border border-v2-secondary rounded text-sm",
            "inline-flex items-center",
            "hover:border-v2-primary hover:text-v2-primary",
            {
              "h-8": size === "sm",
              "h-9": size === "md",
              "h-10": size === "lg",
              "border-v2-focus ring-2 ring-v2-cosmic/200 hover:border-v2-focus":
                hasFocus && !disabled,
              "pointer-events-none text-v2-disabled border-v2-tertiary":
                disabled,
              "pl-3": leftIcon,
              "pr-3": rightIcon,
              "gap-2": leftIcon || rightIcon,
            },
          ),
          wrapperClassName,
        )}
      >
        {leftIcon && (
          <span
            className={twMerge(
              clsx("shrink-0 text-v2-secondary", {
                "text-v2-primary": value,
                "text-v2-disabled": disabled,
              }),
            )}
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          {...props}
          disabled={disabled}
          type={type}
          value={value}
          className={twMerge(
            clsx(
              "pl-3 pr-3 rounded h-full w-full flex-1 bg-v2-primary",
              "group-hover:placeholder:text-v2-primary",
              "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
              {
                "pl-0": leftIcon,
                "pr-0": rightIcon,
                "placeholder:text-v2-primary": hasFocus,
              },
            ),
            className,
          )}
          onFocus={(e) => {
            setHasFocus(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setHasFocus(false);
            onBlur?.(e);
          }}
        />
        {rightIcon && (
          <span
            className={twMerge(
              clsx("shrink-0 text-v2-secondary", {
                "text-v2-primary": value,
                "text-v2-disabled": disabled,
              }),
            )}
          >
            {rightIcon}
          </span>
        )}
      </div>
    );
  },
);
